import { ALLOCATION_SKILLS } from "@/constants/allocations";
import { AllocationSkillId, ShiftCapsPayload, ValidationErrorResponse, WorkerAllocation } from "@/types/allocations";
import moment from "moment";
import Papa from "papaparse";

export const formatShiftTime = (isoString: string): string => {
  if (!isoString) return "";
  const m = moment(isoString);
  return m.isValid() ? m.format("HH:mm") : "";
};

export const formatShiftDate = (isoString: string): string => {
  return moment(isoString).format("DD/MM/YYYY");
};

export const parseTimeToISO = (time: string, referenceDate: string): string => {
  if (!time) return "";
  const [hours, minutes] = time.split(":").map(Number);
  const date = moment(referenceDate);
  date.hours(hours);
  date.minutes(minutes);
  date.seconds(0);
  // Emit the entered wall-clock time as UK local time (no UTC "Z" suffix), so
  // the backend stores the shift time exactly as the user entered it. Using
  // toISOString() here converted to UTC and shifted shifts by 1h during BST.
  return date.format("YYYY-MM-DDTHH:mm:ss");
};

export const parseCsvToShiftCaps = (file: File): Promise<ShiftCapsPayload[]> => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        const payloads: ShiftCapsPayload[] = [];
        const headers = results.meta.fields ?? [];
        const headerMap = new Map(headers.map((h) => [h.toLowerCase(), h]));

        const skillColumnMap = new Map<string, string>();
        for (const [id, name] of Object.entries(ALLOCATION_SKILLS)) {
          const csvCol = headerMap.get(name.toLowerCase());
          if (csvCol) {
            skillColumnMap.set(id, csvCol);
          } else if (headers.includes(id)) {
            skillColumnMap.set(id, id);
          }
        }

        const userIdKey = headerMap.get("userid") ?? "userId";
        const shiftStartKey = headerMap.get("shiftstart") ?? "shiftStart";
        const shiftEndKey = headerMap.get("shiftend") ?? "shiftEnd";

        for (const row of results.data as Record<string, string>[]) {
          const userId = parseInt(row[userIdKey], 10);
          if (Number.isNaN(userId)) continue;

          const allocationSkills: { [skillId: string]: number } = {};
          for (const [skillId, csvCol] of skillColumnMap) {
            allocationSkills[skillId] = parseInt(row[csvCol], 10) || 0;
          }

          payloads.push({
            userId,
            shiftStart: row[shiftStartKey] || null,
            shiftEnd: row[shiftEndKey] || null,
            allocationSkills,
          });
        }

        resolve(payloads);
      },
      error: (error) => reject(error),
    });
  });
};

export const parseMissingAgentIds = (errors: ValidationErrorResponse): number[] | null => {
  const missingAgentIds = errors.missingAgentIds;
  if (!missingAgentIds) return null;
  const ids = missingAgentIds
    .split(",")
    .map((s) => parseInt(s.trim()))
    .filter((n) => !Number.isNaN(n));
  return ids.length > 0 ? ids : null;
};

export const hasOnlyMissingAgentErrors = (errors: ValidationErrorResponse): boolean => {
  const keys = Object.keys(errors);
  return keys.length === 1 && keys[0] === "missingAgentIds";
};

export const formatNonAgentValidationErrors = (
  errors: ValidationErrorResponse,
  workers: { userId: number; name: string }[]
): string[] => {
  const filtered = Object.fromEntries(Object.entries(errors).filter(([key]) => key !== "missingAgentIds"));
  return formatValidationErrors(filtered, workers);
};

const VALIDATION_ERROR_LABELS: Record<string, string> = {
  missingSkillIds: "At least one agent must have a cap of 1 or more for",
  missingAgentIds: "Missing agents",
};

export const formatValidationErrors = (
  errors: ValidationErrorResponse,
  workers: { userId: number; name: string }[]
): string[] => {
  return Object.entries(errors).map(([key, value]) => {
    const label =
      VALIDATION_ERROR_LABELS[key] ??
      key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (s) => s.toUpperCase())
        .trim();
    if (key === "missingSkillIds") {
      const ids = value
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n));
      const names = ids.map((id) => ALLOCATION_SKILLS[id as AllocationSkillId] ?? String(id));
      return `${label}: ${names.join(", ")}`;
    }
    if (key === "missingAgentIds") {
      const ids = value
        .split(",")
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n));
      const names = ids.map((id) => workers.find((w) => w.userId === id)?.name ?? String(id));
      return `${label}: ${names.join(", ")}`;
    }
    return `${label}: ${value}`;
  });
};
export type AllocationSortOrder = "asc" | "desc";

export const sortAllocationsData = (
  data: WorkerAllocation[],
  sortBy: string,
  sortOrder: AllocationSortOrder
): WorkerAllocation[] => {
  return [...data].sort((a, b) => {
    const multiplier = sortOrder === "asc" ? 1 : -1;

    if (sortBy === "name" || sortBy === "department") {
      const aValue = a[sortBy] || "";
      const bValue = b[sortBy] || "";
      return multiplier * aValue.localeCompare(bValue);
    }

    if (sortBy === "shiftStart" || sortBy === "shiftEnd") {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      if (!aValue && !bValue) return 0;
      if (!aValue) return multiplier;
      if (!bValue) return -multiplier;
      return multiplier * (new Date(aValue).getTime() - new Date(bValue).getTime());
    }

    if (sortBy.startsWith("skill_")) {
      const skillId = parseInt(sortBy.replace("skill_", "")) as AllocationSkillId;
      const aValue = a.allocationSkills[skillId] ?? 0;
      const bValue = b.allocationSkills[skillId] ?? 0;
      return multiplier * (aValue - bValue);
    }

    return 0;
  });
};
